import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  decidePlaybook,
  playbooksQuery,
  type AiScope,
  type PlaybookDecision,
  type PlaybookKind,
  type PlaybookRow,
} from '../../api/settingsAiAgent'
import { Spinner } from '../../ui/Spinner'
import { failed, NoteText, type Note } from './parts'

/**
 * Fila "Playbooks" (F11): ce a propus biroul noaptea din rezolvarile care au
 * tinut, decis de un om. Nimic nu se publica singur; `share` si
 * "Mark desk-executable" le poate face numai un Global Admin - butonul se
 * arata dezactivat, poarta e pe server.
 */

const KIND_LABEL: Record<PlaybookKind, string> = {
  answer: 'answer',
  article: 'article draft',
  runbook_safe: 'runbook desk-executable',
  change_standard: 'standard change',
}

const KIND_COLOR: Record<PlaybookKind, string> = {
  answer: '#2f6fb0',
  article: '#3c8d5a',
  runbook_safe: '#b5651d',
  change_standard: '#7a4fa3',
}

export function PlaybooksTab({ scope }: { scope: AiScope }) {
  const query = useQuery(playbooksQuery(scope))
  const client = useQueryClient()
  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'ai_agent', 'playbooks'] })

  if (query.isPending) return <Spinner />
  if (query.isError || !query.data) {
    return <p className="text-danger">{failed(query.error)}</p>
  }
  const page = query.data
  const isCompany = page.scope === 'company'

  return (
    <>
      <p className="text-muted" style={{ fontSize: 13 }} data-testid="playbooks-note">
        Nothing here changes what the desk may do. An accepted playbook is text the desk reads; marking a runbook
        desk-executable is the same switch as in Runbooks.
      </p>

      <div className="panel panel-default">
        <div className="panel-heading">
          <strong>Proposed</strong>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
            from requests resolved at least twice and confirmed by the person, and from approvals that keep repeating
          </span>
        </div>
        <div className="panel-body" style={{ overflowX: 'auto' }}>
          {!page.ready ? (
            <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
              Needs the <code>Version20261104134500_DeskOps</code> migration.
            </p>
          ) : page.proposed.length === 0 ? (
            <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
              Nothing to propose yet. Proposals are written every night.
            </p>
          ) : (
            <table className="table" style={{ fontSize: 13, margin: 0 }}>
              <thead>
                <tr>
                  {isCompany ? null : <th>Company</th>}
                  <th>Kind</th>
                  <th>Request type</th>
                  <th style={{ width: '40%' }}>Proposal</th>
                  <th>Evidence</th>
                  <th>Since</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {page.proposed.map((row) => (
                  <ProposalRow key={row.id} row={row} isCompany={isCompany} isOwner={page.isOwner} onDecided={reload} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="panel panel-default">
        <div className="panel-heading">
          <strong>Decided</strong>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
            the last 50, with who and when
          </span>
        </div>
        <div className="panel-body" style={{ overflowX: 'auto' }}>
          {page.decided.length === 0 ? (
            <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
              No decision yet.
            </p>
          ) : (
            <table className="table table-condensed" style={{ fontSize: 12, margin: 0 }}>
              <thead>
                <tr>
                  {isCompany ? null : <th>Company</th>}
                  <th>Kind</th>
                  <th>Title</th>
                  <th>Decision</th>
                  <th>By</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {page.decided.map((row) => (
                  <tr key={row.id}>
                    {isCompany ? null : <td>{row.company_id === 0 ? 'every company' : row.company || '#' + row.company_id}</td>}
                    <td>
                      <KindChip kind={row.kind} />
                    </td>
                    <td>
                      {row.title}
                      {row.article_id ? <span className="text-muted"> (article #{row.article_id}, unpublished)</span> : null}
                    </td>
                    <td>{row.state}</td>
                    <td>{row.decided_by || '-'}</td>
                    <td>{row.decided_at.substring(0, 16)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}

function KindChip({ kind }: { kind: PlaybookKind }) {
  return (
    <span className="label" style={{ background: KIND_COLOR[kind], color: '#fff', fontWeight: 500 }}>
      {KIND_LABEL[kind]}
    </span>
  )
}

function ProposalRow({
  row,
  isCompany,
  isOwner,
  onDecided,
}: {
  row: PlaybookRow
  isCompany: boolean
  isOwner: boolean
  onDecided(): void
}) {
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(row.title)
  const [body, setBody] = useState(row.body)
  const [note, setNote] = useState<Note | null>(null)
  const [done, setDone] = useState(false)

  const send = (decision: PlaybookDecision, text?: { title: string; body: string }) => {
    setNote({ text: '…' })
    void decidePlaybook(row.id, decision, text)
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setDone(true)
        setNote({
          text:
            r.task ??
            (r.article_id ? `Draft article #${r.article_id} written, unpublished` : decision === 'share' ? 'Shared' : decision === 'dismiss' ? 'Dismissed' : 'Accepted'),
        })
        onDecided()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const ownerOnly = 'Only a Global Admin marks a runbook desk-executable.'

  return (
    <tr>
      {isCompany ? null : <td>{row.company || '#' + row.company_id}</td>}
      <td>
        <KindChip kind={row.kind} />
      </td>
      <td>{row.request_type}</td>
      <td>
        <strong>{row.title}</strong>
        {editing ? (
          <div style={{ marginTop: 6 }}>
            <input
              type="text"
              className="form-control input-sm"
              aria-label="Playbook title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
              className="form-control input-sm"
              aria-label="Playbook text"
              rows={6}
              style={{ marginTop: 4 }}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
        ) : (
          <div>
            <button type="button" className="btn btn-link btn-xs" style={{ padding: 0 }} onClick={() => setOpen(!open)}>
              {open ? 'Hide' : 'Show'}
            </button>
            {open || row.kind === 'runbook_safe' ? (
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 12, marginTop: 4 }} data-testid={'playbook-body-' + row.id}>
                {row.body}
              </div>
            ) : null}
          </div>
        )}
      </td>
      <td>
        seen {row.seen}×
        {row.tickets.length > 0 ? (
          <div style={{ fontSize: 11 }}>
            {row.tickets.map((t, i) => (
              <span key={t}>
                {i > 0 ? ', ' : ''}
                <a href={`/app/easeedesk/ticket/${t}`} target="_blank" rel="noreferrer">
                  #{t}
                </a>
              </span>
            ))}
          </div>
        ) : null}
      </td>
      <td>{row.created_at.substring(0, 10)}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {row.kind === 'runbook_safe' ? (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={done || !isOwner}
            title={isOwner ? 'Same switch as in Runbooks: the command hash and your name go in the audit chain' : ownerOnly}
            onClick={() => send('accept')}
          >
            Mark desk-executable
          </button>
        ) : row.kind === 'change_standard' ? (
          <>
            <a className="btn btn-default btn-sm" href="/app/settings/easeedesk/change_management" target="_blank" rel="noreferrer">
              Open change types
            </a>{' '}
            <button type="button" className="btn btn-primary btn-sm" disabled={done} onClick={() => send('accept')}>
              Accept
            </button>
          </>
        ) : editing ? (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={done || title.trim() === '' || body.trim() === ''}
            onClick={() => send('accept', { title, body })}
          >
            {row.kind === 'article' ? 'Create draft article' : 'Accept'}
          </button>
        ) : (
          <button type="button" className="btn btn-primary btn-sm" disabled={done} onClick={() => setEditing(true)}>
            Accept
          </button>
        )}{' '}
        <button type="button" className="btn btn-default btn-sm" disabled={done} onClick={() => send('dismiss')}>
          Dismiss
        </button>
        {row.kind === 'answer' && isOwner && !isCompany ? (
          <>
            {' '}
            <button
              type="button"
              className="btn btn-default btn-sm"
              disabled={done}
              title="To every company of the provider"
              onClick={() => send('share', editing ? { title, body } : undefined)}
            >
              Share
            </button>
          </>
        ) : null}
        <div style={{ fontSize: 12, marginTop: 4, whiteSpace: 'normal' }}>
          <NoteText note={note} />
        </div>
      </td>
    </tr>
  )
}
